import { resolveCollectionSlug } from '@/domain/market/collection-slug.js';
import {
  buildMinuteUpdownSlug,
  parseMinuteUpdownSlug,
  resolveParsedUpdownRowWindow,
  resolveUpdownRoundNearToleranceSec,
  UPDOWN_MINUTE_ASSETS,
  inferAssetFromTitle,
  resolveUpdownRoundSlugSource,
  toUpdownSeriesCollSlug,
  toUpdownCryptoPageCollSlug,
  toUpdownRoundCollSlug,
  isUpdownMinuteAsset,
  updownShortAssetToFull,
} from '@/domain/crypto/crypto-minute-slug.js';
import {
  resolveLiveEt4hStartSec,
  resolveLiveEtHourStartSec,
  resolveLiveEtNoonStartSec,
  buildHourlyEtUpdownSlugFromStart,
  getEtDateParts,
} from '@/domain/crypto/crypto-updown-et-time.js';
import {
  isMarketResolved,
  parseMarketOutcomes,
  parseOutcomePrices,
  resolveWinningOutcome,
} from '@/utils/market-outcomes';
import { resolveMarketTradeSymbol } from '@/utils/market-detail-map';

export {
  extractUpdownRelatedSearchKeyword,
  extractUpdownSeriesSearchKeyword,
  splitUpdownTitle,
} from '@/domain/crypto/crypto-updown-title-split.js';

export function parseUpdownIntervalSeconds(interval) {
  const raw = String(interval ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return 300;
  if (raw === '1d' || raw === 'daily') return 86400;
  const hourMatch = raw.match(/^(\d+)h$/i);
  if (hourMatch) {
    const hours = Number(hourMatch[1]);
    if (Number.isFinite(hours) && hours > 0) return Math.max(hours * 3600, 3600);
  }
  const minMatch = raw.match(/^(\d+)m$/i);
  if (minMatch) {
    const mins = Number(minMatch[1]);
    if (Number.isFinite(mins) && mins > 0) return Math.max(mins * 60, 60);
  }
  return 300;
}

/**
 * 从完整 slug（btc-updown-15m-{ts}）或系列键（btc-updown-15m / btc-updown-4h）解析周期秒。
 * 解析失败返回 0（调用方再兜底），避免误当成 5m。
 */
export function resolveUpdownIntervalSecFromSlugOrSeries(slugOrSeriesKey) {
  const raw = String(slugOrSeriesKey ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return 0;

  const parsed = parseMinuteUpdownSlug(raw);
  if (parsed?.interval) return parseUpdownIntervalSeconds(parsed.interval);

  if (/-up-or-down-1h$/i.test(raw)) return 3600;
  if (/-up-or-down$/i.test(raw)) return 86400;

  const seriesMatch = raw.match(/-updown-(\d+[mh])$/i);
  if (seriesMatch) return parseUpdownIntervalSeconds(seriesMatch[1]);

  return 0;
}

/** 5m / 4h / 1d 展示标签 */
export function formatUpdownIntervalLabel(intervalSec) {
  const sec = Math.max(Number(intervalSec) || 300, 60);
  if (sec >= 86400 && sec % 86400 === 0) return `${sec / 86400}d`;
  if (sec % 3600 === 0) return `${sec / 3600}h`;
  return `${sec / 60}m`;
}

/** 时间轴 / 拉取：live 之前展示过去档数（无 collection 的档不展示） */
export const UPDOWN_ROUND_WINDOW_BEFORE = 3;
/** @deprecated 展示窗口改由 API 返回全量；保留兼容 expandUpdownRoundWindow */
export const UPDOWN_ROUND_WINDOW_AFTER = 8;
/** 时间轴：live 之后展示未来档数（过去用 UPDOWN_ROUND_WINDOW_BEFORE） */
export const UPDOWN_TIMELINE_WINDOW_AFTER = 7;

/**
 * 详情时间轴：未来档静默轮询间隔（ms）。
 * 5m/15m→45s；1h→90s；≥4h→120s。仅关联 collection，不预拉交易壳。
 */
export function resolveUpdownFutureTimelinePollMs(intervalSec) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  if (interval >= 4 * 3600) return 120_000;
  if (interval >= 3600) return 90_000;
  return 45_000;
}

/** cryptoPageBySlug：当前时间向后默认 12 小时（长周期由 resolveUpdownSlugLookaheadSec 覆盖） */
export const UPDOWN_SLUG_LOOKAHEAD_SEC = 12 * 3600;

/** 盘口/报价保留到倒计时归零，轮次结束后立即停止。 */
export const UPDOWN_PM_QUOTE_HALT_SEC = 0;

/**
 * 轮次结束防抖：判定 ended 后，校时回跳导致 remaining ≤ 此值仍视为已结束，
 * 避免交易区 / auto-live 在边界来回闪。
 */
export const UPDOWN_ROUND_ENDED_HYSTERESIS_SEC = 5;

/**
 * @param {number} endSec Unix 秒
 * @param {number} nowSec Unix 秒
 * @param {boolean} [latched] 上一轮是否已 ended
 */
export function resolveUpdownRoundEndedWithHysteresis(endSec, nowSec, latched = false) {
  const end = Number(endSec);
  const now = Number(nowSec);
  if (!Number.isFinite(end) || end <= 0 || !Number.isFinite(now)) return Boolean(latched);
  const remaining = end - now;
  if (remaining <= 0) return true;
  if (latched && remaining <= UPDOWN_ROUND_ENDED_HYSTERESIS_SEC) return true;
  return false;
}

/** @param {number} endSec Unix 秒 @param {number} [nowMs] */
export function isUpdownPmQuoteStreamingAllowed(endSec, nowMs = Date.now()) {
  const end = Number(endSec);
  if (!Number.isFinite(end) || end <= 0) return true;
  const displayRemainingSec = Math.floor(Math.max(end * 1000 - nowMs, 0) / 1000);
  return displayRemainingSec > UPDOWN_PM_QUOTE_HALT_SEC;
}

/**
 * 首屏 K 线 / 现货回看档数（性能优先：先少拉，左滑再分页）。
 * 5m→48×5m≈4h；15m→48×15m≈12h
 */
export const UPDOWN_CHART_LOOKBACK_BARS = 48;

/** 左滑分页：每次再拉约 48 根 K */
export const UPDOWN_CANDLE_PAGE_BARS = 48;

/** @deprecated 使用 resolveUpdownChartLookbackSec(intervalSec) */
export const UPDOWN_CHART_LOOKBACK_SEC = 48 * 300;

/** 首屏回看：5m→4h；15m→12h；1h→24h；4h→3d；1d→7d */
export function resolveUpdownChartLookbackSec(intervalSec) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  if (interval >= 86400) return 7 * 86400;
  if (interval >= 4 * 3600) return 3 * 86400;
  if (interval >= 3600) return 24 * 3600;
  return interval * UPDOWN_CHART_LOOKBACK_BARS;
}

/** 分页可拉到的最大回看：5m→24h；15m→3d；1h→7d；4h→14d；1d→30d */
export function resolveUpdownChartMaxLookbackSec(intervalSec) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  if (interval >= 86400) return 30 * 86400;
  if (interval >= 4 * 3600) return 14 * 86400;
  if (interval >= 3600) return 7 * 86400;
  if (interval >= 900) return 3 * 86400;
  return 24 * 3600;
}

/** 单页时长（秒） */
export function resolveUpdownCandlePageSec(intervalSec) {
  return resolveChainlinkCandleSec(intervalSec) * UPDOWN_CANDLE_PAGE_BARS;
}

/** PM chainlink-candles interval（5m 盘→5m，≥15m→15m） */
export function resolveChainlinkCandleInterval(intervalSec) {
  const sec = Math.max(Number(intervalSec) || 300, 60);
  return sec >= 900 ? '15m' : '5m';
}

/** PM chainlink-candles 上限（API 仅接受 limit=30，更大值会 400） */
export const CHAINLINK_CANDLES_API_MAX_LIMIT = 30;

/** PM chainlink-candles 默认拉 30 根 K 线 */
export function resolveChainlinkCandleLimit() {
  return CHAINLINK_CANDLES_API_MAX_LIMIT;
}

/** chainlink-candles 单根 K 线宽度（秒） */
export function resolveChainlinkCandleSec(intervalSec) {
  return resolveChainlinkCandleInterval(intervalSec) === '15m' ? 900 : 300;
}

/** X 轴 rangeKey：短窗用 1H，长窗用 6H/12H/1D/1W */
export function resolveUpdownChartRangeKey(lookbackSec) {
  const span = Math.max(Number(lookbackSec) || 3600, 60);
  if (span <= 3600) return '1H';
  if (span <= 6 * 3600) return '6H';
  if (span <= 24 * 3600) return '12H';
  if (span <= 7 * 86400) return '1D';
  return '1W';
}

/** 长周期 slug 页向前/后窗口 */
export function resolveUpdownSlugLookaheadSec(intervalSec) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  if (interval >= 86400) return 7 * 86400;
  if (interval >= 4 * 3600) return 2 * 86400;
  return UPDOWN_SLUG_LOOKAHEAD_SEC;
}

/** 按 interval 估算 pageSize（覆盖 -4X ~ lookahead） */
export function resolveCryptoUpdownSlugPageSize(intervalSec) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const lookahead = resolveUpdownSlugLookaheadSec(interval);
  const spanSec = UPDOWN_ROUND_WINDOW_BEFORE * interval + lookahead;
  return Math.min(200, Math.ceil(spanSec / interval) + 8);
}

export { resolveUpdownRoundNearToleranceSec } from '@/domain/crypto/crypto-minute-slug.js';

/** 按 interval 对齐的当前 live 轮次开始时间（秒）；1h/4h/1d 用 ET 网格 */
export function resolveLiveRoundStartSec(nowSec, intervalSec) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const now = Number(nowSec);
  if (!Number.isFinite(now) || now <= 0) return 0;

  if (interval >= 86400) return resolveLiveEtNoonStartSec(now);
  if (interval >= 4 * 3600) return resolveLiveEt4hStartSec(now);
  if (interval >= 3600) return resolveLiveEtHourStartSec(now);

  return Math.floor(now / interval) * interval;
}

/** 轮次尚未开始（未来盘） */
export function isUpdownRoundFuture(roundTs, nowSec) {
  const start = Number(roundTs);
  const now = Number(nowSec);
  if (!Number.isFinite(start) || start <= 0 || !Number.isFinite(now)) return false;
  return now < start;
}

/** 轮次正在进行中 */
export function isUpdownRoundLive(roundTs, nowSec, intervalSec) {
  const start = Number(roundTs);
  const now = Number(nowSec);
  const interval = Math.max(Number(intervalSec) || 300, 60);
  if (!Number.isFinite(start) || start <= 0) return false;
  return start <= now && now < start + interval;
}

/** 两个 roundTs 是否同一档（默认 ±60s，5m 盘足够覆盖 slug/API 偏差） */
export function isUpdownRoundTsNear(roundTsA, roundTsB, toleranceSec = 60) {
  const a = Number(roundTsA);
  const b = Number(roundTsB);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return false;
  return Math.abs(a - b) <= Math.max(Number(toleranceSec) || 60, 1);
}

/** collection 是否对应请求的 market / collection id */
export function updownCollectionIdentityMatches(collection, requestedId) {
  const requested = String(requestedId ?? '').trim();
  if (!requested || !collection) return false;
  const ids = [
    collection.id,
    collection.collectionId,
    collection.markets?.[0]?.collectionId,
    collection.markets?.[0]?.id,
  ]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean);
  return ids.includes(requested);
}

/** 轮次切换后校验：只读已加载 collection 的 slug / endDate，与目标 roundTs 比对 */
export function verifyUpdownRoundLoaded(collection, targetRoundTs, intervalSec = 300) {
  const targetTs = Number(targetRoundTs);
  if (!collection || !Number.isFinite(targetTs) || targetTs <= 0) return false;

  const interval = Math.max(Number(intervalSec) || 300, 60);
  const viewing = resolveViewingUpdownRound(collection, '', interval);
  const loadedTs = Number(viewing?.roundTs) || 0;
  if (!loadedTs) return false;

  const tolerance = resolveUpdownRoundNearToleranceSec(interval);
  return isUpdownRoundTsNear(loadedTs, targetTs, tolerance);
}

/**
 * cryptoPageBySlug 查询体：以 viewing 轮次为中心，向前/后各若干档
 * @param {string} seriesKey 如 btc-updown-5m
 * @param {number} anchorRoundTs 当前查看轮次的 roundTs
 */
export function buildCryptoUpdownViewingSeriesQuery(
  seriesKey,
  anchorRoundTs,
  intervalSec,
  { before = UPDOWN_ROUND_WINDOW_BEFORE, after = 10 } = {}
) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const anchor = Number(anchorRoundTs);
  const collSlug = toUpdownCryptoPageCollSlug(seriesKey);
  if (!collSlug || !Number.isFinite(anchor) || anchor <= 0) return null;

  return {
    collSlug,
    startTime: anchor - before * interval,
    endTime: anchor + after * interval,
  };
}

/**
 * cryptoPageBySlug 查询体：以当前时间为中心，-4X ~ +12h
 * @param {string} seriesKey 如 btc-updown-5m
 */
export function buildCryptoUpdownSlugPageQuery(seriesKey, nowSec, intervalSec) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const now = Number(nowSec);
  const collSlug = toUpdownCryptoPageCollSlug(seriesKey);
  if (!collSlug || !Number.isFinite(now) || now <= 0) return null;

  return {
    collSlug,
    startTime: now - UPDOWN_ROUND_WINDOW_BEFORE * interval,
    endTime: now + resolveUpdownSlugLookaheadSec(interval),
  };
}

/**
 * 时间轴专用窗：过去 before 档 + 当前 live + 未来 after 档。
 * slug/endDate 语义下 endTime 需盖住「未来第 after 档」的收盘 ts（= start + (after+1)*interval）。
 * @param {string} seriesKey
 * @param {number} liveStartSec clock live 开盘
 * @param {number} intervalSec
 * @param {{ before?: number, after?: number }} [options]
 */
export function buildCryptoUpdownTimelineWindowQuery(
  seriesKey,
  liveStartSec,
  intervalSec,
  { before = UPDOWN_ROUND_WINDOW_BEFORE, after = UPDOWN_TIMELINE_WINDOW_AFTER } = {}
) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const liveStart = Number(liveStartSec);
  const collSlug = toUpdownCryptoPageCollSlug(seriesKey);
  const beforeN = Math.max(Number(before) || 0, 0);
  const afterN = Math.max(Number(after) || 0, 0);
  if (!collSlug || !Number.isFinite(liveStart) || liveStart <= 0) return null;

  return {
    collSlug,
    startTime: liveStart - beforeN * interval,
    // 未来第 after 档开盘 = liveStart+after*I，收盘 = liveStart+(after+1)*I（slug=end）
    endTime: liveStart + (afterN + 1) * interval,
  };
}

/**
 * 同轮次查询：collSlug 用目标档完整 slug（btc-updown-5m-{roundTs}），
 * 勿带历史档旧 ts；系列列表仍用前缀 btc-updown-5m。
 * 时间窗：上一档最后一秒 → 下一档第一秒。
 * 例：5m、now=xx:06、liveStart=xx:05 → startTime=xx:05-1s，endTime=xx:10。
 * @param {string} collSlug 系列前缀或任意完整 slug（会规范化）
 */
export function buildCryptoUpdownSameRoundQuery(collSlug, roundTs, intervalSec) {
  const start = Number(roundTs);
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const roundSlug = toUpdownRoundCollSlug(collSlug, start);
  if (!start || !roundSlug) return null;

  return {
    collSlug: roundSlug,
    startTime: start - 1,
    endTime: start + interval,
  };
}

/** 从 slug / collection / title / 路由 sk 推断系列 key（btc-updown-5m） */
export function resolveUpdownSeriesKey({
  eventSlug = '',
  collection = null,
  displayTitle = '',
  routeSeriesKey = '',
} = {}) {
  const fromRoute = toUpdownSeriesCollSlug(routeSeriesKey);
  if (fromRoute) return fromRoute;

  const slug = String(eventSlug ?? '').trim() || resolveUpdownRoundSlugSource(collection);
  const fromSlug = toUpdownSeriesCollSlug(slug);
  if (fromSlug) return fromSlug;

  const title = String(displayTitle ?? collection?.title ?? collection?.question ?? '').trim();
  const asset = inferAssetFromTitle(title);
  if (!isUpdownMinuteAsset(asset)) return '';
  const interval = parseMinuteUpdownSlug(slug)?.interval || '5m';
  return `${asset}-updown-${interval}`;
}

/** 从 API 原始行中匹配目标 roundTs（允许小偏差；禁止相邻档时间窗重叠误命中） */
export function pickUpdownRoundFromRows(
  rows,
  roundTs,
  intervalSec,
  seriesKey = '',
  { strict = false } = {}
) {
  const targetTs = Number(roundTs);
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const key = String(seriesKey ?? '')
    .trim()
    .toLowerCase();
  if (!Number.isFinite(targetTs) || targetTs <= 0) return null;

  const mapped = (rows ?? []).map(mapRawToUpdownRound).filter((row) => row.collectionId);
  const nearTol = resolveUpdownRoundNearToleranceSec(interval);

  let match = mapped.find((row) => resolveUpdownRoundTs(row) === targetTs);
  if (match) return match;

  match = mapped.find((row) => {
    const ts = resolveUpdownRoundTs(row);
    return ts > 0 && Math.abs(ts - targetTs) <= 60;
  });
  if (match) return match;

  if (key) {
    match = mapped.find((row) => {
      if (row.seriesKey !== key && !String(row.seriesKey ?? '').startsWith(key.split('-')[0])) {
        return false;
      }
      const ts = resolveUpdownRoundTs(row);
      return ts > 0 && isUpdownRoundTsNear(ts, targetTs, nearTol);
    });
    if (match) return match;
  }

  if (strict) return null;
  // 非 strict 仍须贴近目标 ts；禁止「随便返回第一行」（历史盘 unshift 后会把 go-live 钉死在当前档）
  return (
    mapped.find((row) => {
      const ts = resolveUpdownRoundTs(row);
      return ts > 0 && isUpdownRoundTsNear(ts, targetTs, nearTol);
    }) ?? null
  );
}

/** 相关市场：除当前资产外各币种 collSlug 列表 */
export function buildRelatedUpdownCollSlugs(
  currentAsset,
  intervalLabel = '5m',
  { hourlyEt = false } = {}
) {
  const interval = String(intervalLabel ?? '5m')
    .trim()
    .toLowerCase();
  const current = String(currentAsset ?? '')
    .trim()
    .toLowerCase();
  if (!interval) return [];

  const others = UPDOWN_MINUTE_ASSETS.filter((asset) => asset !== current);
  if (hourlyEt) {
    return others.map((asset) => `${updownShortAssetToFull(asset)}-up-or-down-1h`);
  }

  return others.map((asset) => `${asset}-updown-${interval}`);
}

/** PM Gamma 批量 slug：同开盘 roundTs 各资产（slug 埋入结束 ts） */
export function buildRelatedUpdownGammaSlugs(
  roundTs,
  intervalLabel = '5m',
  currentAsset = '',
  { hourlyEt = false } = {}
) {
  const interval = String(intervalLabel ?? '5m')
    .trim()
    .toLowerCase();
  const ts = Number(roundTs);
  const current = String(currentAsset ?? '')
    .trim()
    .toLowerCase();
  if (!interval || !Number.isFinite(ts) || ts <= 0) return [];

  const others = UPDOWN_MINUTE_ASSETS.filter((asset) => asset !== current);
  if (hourlyEt) {
    return others
      .map((asset) => buildHourlyEtUpdownSlugFromStart(updownShortAssetToFull(asset), ts))
      .filter(Boolean);
  }

  return others.map((asset) => buildMinuteUpdownSlug(asset, interval, ts)).filter(Boolean);
}

function parseIsoUtcSec(iso) {
  const ms = Date.parse(String(iso ?? '').trim());
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

/**
 * 从 ticker / slug 或 startDate/endDate 推断当前 viewing 轮次（roundTs = 开盘）。
 * interval slug：先按 venue 默认解析，再用 market.endDate 纠正开/收盘歧义
 * （PM 埋开盘 unix，SELF 埋收盘）；跨天系列起止离 slug 很远时不覆盖，避免倒计时 > interval。
 */
export function resolveViewingUpdownRound(collection, eventSlug, intervalSec = 300) {
  const slug =
    String(eventSlug ?? '').trim() ||
    resolveUpdownRoundSlugSource(collection) ||
    resolveCollectionSlug(collection);
  const parsed = parseMinuteUpdownSlug(slug);
  const market = Array.isArray(collection?.markets) ? collection.markets[0] : null;
  const apiEndSec = parseIsoUtcSec(market?.endDate ?? collection?.endDate);
  const apiStartSec = parseIsoUtcSec(
    market?.eventStartTime ??
      market?.startDate ??
      collection?.eventStartTime ??
      collection?.startDate
  );

  const interval = parsed
    ? parseUpdownIntervalSeconds(parsed.interval || '5m')
    : Math.max(Number(intervalSec) || 300, 60);
  const knownWindow = resolveParsedUpdownRowWindow(collection, parsed);
  if (knownWindow) return knownWindow;

  // 无 interval slug：保持历史行为（hourly ET / daily / 旧行），避免改动非 5m/15m 路径
  if (apiStartSec != null && apiEndSec != null && apiEndSec > apiStartSec) {
    return {
      roundTs: apiStartSec,
      endSec: apiEndSec,
      intervalSec: Math.max(apiEndSec - apiStartSec, 60),
    };
  }

  if (apiEndSec == null) {
    return { roundTs: 0, endSec: 0, intervalSec: interval };
  }
  return {
    roundTs: apiStartSec ?? Math.max(0, apiEndSec - interval),
    endSec: apiEndSec,
    intervalSec: interval,
  };
}

/** 分钟盘 collection / market 是否已结束（含 closed / 不再接单） */
export function isUpdownCollectionClosed(collection) {
  if (!collection || typeof collection !== 'object') return false;
  const market = Array.isArray(collection.markets) ? collection.markets[0] : null;
  if (isMarketResolved(market, collection)) return true;
  if (market?.acceptingOrders === false) return true;
  return Boolean(collection.closed || market?.closed);
}

/**
 * @param {Record<string, unknown>} raw search / collection 原始行
 */
export function mapRawToUpdownRound(raw) {
  const market = Array.isArray(raw?.markets) ? raw.markets[0] : raw;
  const slug = resolveUpdownRoundSlugSource(raw) || resolveCollectionSlug(raw);
  const parsed = parseMinuteUpdownSlug(slug);
  const title = String(raw?.title ?? market?.question ?? raw?.question ?? '').trim();
  const collectionId = String(raw?.collectionId ?? raw?.id ?? '').trim();
  const interval = parsed?.interval || '5m';
  const intervalSec = parseUpdownIntervalSeconds(interval);
  const apiStartSec = parseIsoUtcSec(
    market?.eventStartTime ?? market?.startDate ?? raw?.eventStartTime ?? raw?.startDate
  );
  const apiEndSec = parseIsoUtcSec(market?.endDate ?? raw?.endDate);

  const knownWindow = resolveParsedUpdownRowWindow(raw, parsed);
  let roundTs = 0;
  if (knownWindow) {
    roundTs = knownWindow.roundTs;
  } else if (apiStartSec != null && apiEndSec != null && apiEndSec > apiStartSec) {
    roundTs = apiStartSec;
  } else if (apiEndSec != null) {
    roundTs = apiEndSec - intervalSec;
  }

  const asset = parsed?.asset || inferAssetFromTitle(title);
  const seriesKey =
    parsed?.seriesKey ||
    (asset && interval && interval !== '1d' ? `${asset}-updown-${interval}` : '');
  const closed = Boolean(raw?.closed) || Boolean(market?.closed) || isMarketResolved(market, raw);
  const outcome = closed
    ? resolveWinningOutcome(
        market?.outcomes ?? raw?.outcomes,
        market?.settlementOutcomePrices ??
          market?.outcomePrices ??
          raw?.settlementOutcomePrices ??
          raw?.outcomePrices
      )
    : '';

  const outcomes = parseMarketOutcomes(market?.outcomes ?? raw?.outcomes);
  const tradeSymbol = resolveMarketTradeSymbol(market ?? raw);
  let yesPct = null;
  if (!closed) {
    const prices = parseOutcomePrices(market?.outcomePrices ?? raw?.outcomePrices);
    if (prices.length >= 1) {
      const yes = prices[0];
      yesPct = Math.max(1, Math.min(99, Math.round(yes <= 1 ? yes * 100 : yes)));
    }
  }

  return {
    collectionId,
    slug,
    ticker:
      String(raw?.ticker ?? '')
        .trim()
        .toLowerCase() || slug,
    title,
    roundTs,
    // Keep embed hints so resolveUpdownRoundTs / resolveViewingUpdownRound on
    // mapped rows still prefer PM start-embed (not bare end-embed slug parse).
    // Retain collection fallback dates; the shared resolver still prefers valid market dates.
    endDate: raw?.endDate ?? market?.endDate ?? null,
    startDate: raw?.startDate ?? market?.startDate ?? null,
    eventStartTime: raw?.eventStartTime ?? null,
    liquiditySource: market?.liquiditySource ?? raw?.liquiditySource ?? null,
    dataSource: market?.dataSource ?? raw?.dataSource ?? null,
    markets: Array.isArray(raw?.markets) ? raw.markets : undefined,
    seriesKey,
    interval: parsed?.interval ?? interval,
    outcome: String(outcome ?? '').trim(),
    closed,
    tradeSymbol,
    outcomes,
    yesPct,
  };
}

export function normalizeUpdownRoundOutcome(outcome) {
  const value = String(outcome ?? '')
    .trim()
    .toLowerCase();
  if (!value) return '';
  if (value === 'up' || value === 'yes' || value === '涨') return 'Up';
  if (value === 'down' || value === 'no' || value === '跌') return 'Down';
  return String(outcome ?? '').trim();
}

export function hasUpdownRoundOutcome(round) {
  return Boolean(normalizeUpdownRoundOutcome(round?.outcome));
}

/** 已结束但 API 未返回结算方向（须有真实 collection，排除时间轴占位档） */
export function isUpdownRoundSettling(round) {
  if (!round?.closed || round?.isLive) return false;
  if (!String(round?.collectionId ?? '').trim()) return false;
  return !hasUpdownRoundOutcome(round);
}

/** 仅附加 UI 标记（isLive）；closed/outcome 以 API 映射为准，不做客户端推断 */
export function enrichUpdownRound(row, nowSec, intervalSec) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const roundTs = resolveUpdownRoundTs(row) || Number(row?.roundTs) || 0;

  return {
    ...row,
    collectionId: String(row?.collectionId ?? row?.id ?? '').trim(),
    roundTs: roundTs || row?.roundTs,
    isLive: isUpdownRoundLive(roundTs, nowSec, interval),
  };
}

function dedupeUpdownRoundsByTimestamp(rows) {
  /** @type {Map<number|string, typeof rows[0]>} */
  const map = new Map();
  for (const row of rows) {
    const key = row.roundTs || row.collectionId;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, row);
      continue;
    }
    if (prev.closed && !row.closed) {
      map.set(key, row);
      continue;
    }
    if (!prev.closed && row.closed) continue;
    map.set(key, row);
  }
  return [...map.values()];
}

export function filterUpdownSeriesRounds(
  rows,
  seriesKey,
  keyword,
  { keepCollectionIds = [] } = {}
) {
  const key = String(seriesKey ?? '')
    .trim()
    .toLowerCase();
  const prefix = String(keyword ?? '')
    .trim()
    .toLowerCase();
  if (!key && !prefix && !keepCollectionIds.length) return [];

  const keepIds = new Set(
    (keepCollectionIds ?? []).map((id) => String(id ?? '').trim()).filter(Boolean)
  );

  const mapped = (rows ?? [])
    .map(mapRawToUpdownRound)
    .filter((row) => {
      if (row.collectionId && keepIds.has(row.collectionId)) return true;
      if (key && row.seriesKey === key) return true;
      if (key && !row.seriesKey) {
        const asset = inferAssetFromTitle(row.title);
        if (asset && key.startsWith(`${asset}-updown-`)) return true;
      }
      if (!prefix) return false;
      return String(row.title ?? '')
        .trim()
        .toLowerCase()
        .startsWith(prefix);
    })
    .filter((row) => row.roundTs > 0 || row.collectionId)
    .filter((row, index, list) => {
      if (!row.collectionId) return true;
      return list.findIndex((item) => item.collectionId === row.collectionId) === index;
    });

  return dedupeUpdownRoundsByTimestamp(mapped).sort((a, b) => {
    if (a.roundTs && b.roundTs) return a.roundTs - b.roundTs;
    return String(a.title).localeCompare(String(b.title));
  });
}

/** 当前时间生效的分钟盘索引；无则取离 now 最近的一档 */
export function resolveLiveRoundIndex(rounds, nowSec, intervalSec) {
  if (!Array.isArray(rounds) || !rounds.length) return -1;

  const activeIdx = rounds.findIndex(
    (row) => row.roundTs > 0 && row.roundTs <= nowSec && nowSec < row.roundTs + intervalSec
  );
  if (activeIdx >= 0) return activeIdx;

  let bestIdx = 0;
  let bestDist = Infinity;
  rounds.forEach((row, idx) => {
    if (!row.roundTs) return;
    const dist = Math.abs(row.roundTs - nowSec);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = idx;
    }
  });
  return bestIdx;
}

/** 以 live 节点为中心，前 before / 后 after 条 */
export function buildUpdownRoundWindow(rounds, centerIdx, { before = 4, after = 4 } = {}) {
  if (!Array.isArray(rounds) || !rounds.length || centerIdx < 0) return [];

  const start = Math.max(0, centerIdx - before);
  const end = Math.min(rounds.length, centerIdx + after + 1);

  return rounds.slice(start, end).map((row, offset) => {
    const index = start + offset;
    return {
      ...row,
      isLive: index === centerIdx,
    };
  });
}

/**
 * 底部时间轴锚点：始终以当前 live 档位为中心（PM 行为）。
 */
export function resolveUpdownTimelineAnchor(nowSec, intervalSec, viewingRoundTs = 0) {
  const liveTs = resolveLiveRoundStartSec(nowSec, intervalSec);
  const viewingTs = Number(viewingRoundTs) || 0;
  return liveTs || viewingTs || 0;
}

function findRoundForSlot(rounds, byTs, slotTs, intervalSec) {
  const ts = Number(slotTs);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  if (byTs.has(ts)) return byTs.get(ts);

  // 仅容忍 slug/API 秒级偏差，避免相邻 interval 档复用同一轮次
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const maxSkew = Math.min(120, Math.floor(interval / 4));

  let best = null;
  let bestDist = Infinity;
  for (const row of rounds ?? []) {
    const rowTs = resolveUpdownRoundTs(row);
    if (!rowTs) continue;
    const dist = Math.abs(rowTs - ts);
    if (dist <= maxSkew && dist < bestDist) {
      best = row;
      bestDist = dist;
    }
  }
  return best;
}

function dedupeUpdownTimelineSlots(slots, preferCollectionId = '') {
  const preferId = String(preferCollectionId ?? '').trim();
  /** @type {Map<number, (typeof slots)[0]>} */
  const byTs = new Map();
  for (const row of slots ?? []) {
    const ts = resolveUpdownRoundTs(row);
    if (!ts) continue;
    const prev = byTs.get(ts);
    if (!prev) {
      byTs.set(ts, row);
      continue;
    }
    const score = (item) => {
      let s = 0;
      const id = String(item?.collectionId ?? '').trim();
      if (id) s += 2;
      if (preferId && id === preferId) s += 4;
      if (item?.isLive) s += 1;
      if (item?.closed && hasUpdownRoundOutcome(item)) s += 1;
      return s;
    };
    if (score(row) >= score(prev)) byTs.set(ts, row);
  }
  return [...byTs.values()].sort((a, b) => resolveUpdownRoundTs(a) - resolveUpdownRoundTs(b));
}

/**
 * 以 anchorRoundTs 为中心补齐时间档；已有 API 行保留 collectionId，其余为占位。
 */
export function expandUpdownRoundWindow(
  rounds,
  anchorRoundTs,
  intervalSec,
  { before = 10, after = 10, nowSec = Math.floor(Date.now() / 1000), preferCollectionId = '' } = {}
) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const anchorTs = Number(anchorRoundTs) || 0;
  if (!anchorTs) {
    return buildUpdownRoundWindow(rounds, resolveLiveRoundIndex(rounds, nowSec, interval), {
      before,
      after,
    });
  }

  /** @type {Map<number, (typeof rounds)[0]>} */
  const byTs = new Map();
  for (const row of rounds ?? []) {
    const ts = resolveUpdownRoundTs(row);
    if (ts) byTs.set(ts, row);
  }

  const liveRound = resolveLiveUpdownRound(rounds, nowSec, interval);

  const slots = [];
  for (let offset = -before; offset <= after; offset += 1) {
    const ts = anchorTs + offset * interval;
    const existing = findRoundForSlot(rounds, byTs, ts, interval);
    if (existing) {
      const tickerTs = resolveUpdownRoundTs(existing);
      slots.push(
        enrichUpdownRound(
          {
            ...existing,
            roundTs: tickerTs || ts,
            isLive: isUpdownRoundLive(ts, nowSec, interval),
          },
          nowSec,
          interval
        )
      );
      continue;
    }

    const isLiveSlot = ts <= nowSec && nowSec < ts + interval;
    if (isLiveSlot && liveRound?.collectionId) {
      const liveTs = resolveUpdownRoundTs(liveRound);
      const maxSkew = Math.min(90, Math.floor(interval / 4));
      if (liveTs && isUpdownRoundTsNear(ts, liveTs, maxSkew)) {
        slots.push(
          enrichUpdownRound(
            {
              ...liveRound,
              roundTs: ts,
              isLive: true,
            },
            nowSec,
            interval
          )
        );
      }
    }
    // 无 collectionId 的时间格不展占位；由 series 静默补关联，补不到则永久不显示
  }

  return dedupeUpdownTimelineSlots(slots, preferCollectionId).filter((row) =>
    Boolean(String(row?.collectionId ?? '').trim())
  );
}

/** API 返回轮次：按 roundTs 排序展示，仅附加 isLive */
export function buildUpdownRoundTimeline(rounds, nowSec, intervalSec) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const now = Number(nowSec);

  return (rounds ?? [])
    .filter((row) => String(row?.collectionId ?? row?.id ?? '').trim())
    .map((row) => enrichUpdownRound(row, now, interval))
    .sort((a, b) => resolveUpdownRoundTs(a) - resolveUpdownRoundTs(b));
}

/** 轮次结束后找下一 collection（优先 live，其次按 roundTs） */
export function resolveNextUpdownRound(rounds, currentRoundTs, nowSec, intervalSec) {
  if (!Array.isArray(rounds) || !rounds.length) return null;

  const liveIdx = resolveLiveRoundIndex(rounds, nowSec, intervalSec);
  if (liveIdx >= 0) {
    const live = rounds[liveIdx];
    if (live?.collectionId && live.roundTs !== currentRoundTs) return live;
  }

  const sorted = rounds
    .filter((row) => row.collectionId && row.roundTs > 0)
    .sort((a, b) => a.roundTs - b.roundTs);

  const byTime = sorted.find((row) => row.roundTs > currentRoundTs);
  if (byTime) return byTime;

  if (currentRoundTs > 0 && intervalSec > 0) {
    const expectedNextTs = currentRoundTs + intervalSec;
    return sorted.find((row) => row.roundTs === expectedNextTs) ?? null;
  }

  return null;
}

/** 当前 live 轮次（须落在当前时间窗口且含 collectionId） */
export function resolveLiveUpdownRound(rounds, nowSec, intervalSec) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const now = Number(nowSec);
  const list = Array.isArray(rounds) ? rounds : [];

  const inWindow = list.find((row) => {
    const ts = resolveUpdownRoundTs(row);
    if (!ts || !String(row?.collectionId ?? '').trim()) return false;
    return ts <= now && now < ts + interval;
  });
  if (inWindow) return inWindow;

  const liveStart = resolveLiveRoundStartSec(now, interval);
  if (!liveStart) return null;

  const exact = list.find(
    (row) => resolveUpdownRoundTs(row) === liveStart && String(row?.collectionId ?? '').trim()
  );
  if (exact) return exact;

  return (
    list.find((row) => {
      const ts = resolveUpdownRoundTs(row);
      if (!ts || !String(row?.collectionId ?? '').trim()) return false;
      return Math.abs(ts - liveStart) <= 120;
    }) ?? null
  );
}

/** 当前时钟 live 轮次的 roundTs（与 interval 对齐） */
export function resolveClockLiveRoundTs(nowSec, intervalSec) {
  return resolveLiveRoundStartSec(nowSec, intervalSec);
}

export function isViewingLiveUpdownRound(liveRound, viewingCollectionId, isRoundEnded) {
  if (isRoundEnded) return false;
  const liveId = String(liveRound?.collectionId ?? '').trim();
  const viewId = String(viewingCollectionId ?? '').trim();
  return Boolean(liveId && viewId && liveId === viewId);
}

export function formatRoundClockLabel(roundTs, locale = 'en') {
  if (!roundTs) return '—';
  const d = new Date(roundTs * 1000);
  const isZh = String(locale || '').startsWith('zh');
  return d.toLocaleTimeString(isZh ? 'zh-CN' : locale || 'en', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
}

/** 日盘结束标签：用结算日（ET）日期，避免误显示成秒级时钟 */
export function formatRoundEndDateLabel(roundTs, intervalSec = 86400, locale = 'en') {
  const start = Number(roundTs);
  const interval = Math.max(Number(intervalSec) || 86400, 60);
  if (!Number.isFinite(start) || start <= 0) return '—';
  const endSec = start + interval;
  const parts = getEtDateParts(endSec);
  const isZh = String(locale || '').startsWith('zh');
  if (isZh) return `${parts.monthIndex + 1}月${parts.day}日`;
  const d = new Date(Date.UTC(parts.year, parts.monthIndex, parts.day, 12, 0));
  return d.toLocaleDateString(locale || 'en', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** 展示用：窗口结束时刻（对齐 PM chips；内部 roundTs 仍为 start） */
export function formatRoundEndClockLabel(roundTs, intervalSec = 300, locale = 'en') {
  const start = Number(roundTs);
  const interval = Math.max(Number(intervalSec) || 300, 60);
  if (!Number.isFinite(start) || start <= 0) return '—';
  if (interval >= 86400) return formatRoundEndDateLabel(start, interval, locale);

  const endSec = start + interval;
  // 1h / 4h：跨日带月日，避免底栏看起来像 5m 只闪结束分秒（详情统一 UTC）
  if (interval >= 3600) {
    const isZh = String(locale || '').startsWith('zh');
    const timeZone = 'UTC';
    const dateOptions = { year: 'numeric', month: '2-digit', day: '2-digit', timeZone };
    const startDay = new Date(start * 1000).toLocaleDateString('en-CA', dateOptions);
    const endDay = new Date(endSec * 1000).toLocaleDateString('en-CA', dateOptions);
    if (startDay !== endDay) {
      return new Date(endSec * 1000).toLocaleString(isZh ? 'zh-CN' : locale || 'en', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone,
      });
    }
  }
  return formatRoundClockLabel(endSec, locale);
}

/** 轮次时间：优先 ticker/slug 开盘；时间轴占位档显式 roundTs 与 slug 不一致时以 roundTs 为准 */
export function resolveUpdownRoundTs(round) {
  const slug =
    resolveUpdownRoundSlugSource(round) ||
    String(round?.slug ?? round?.ticker ?? '').trim();
  const parsed = parseMinuteUpdownSlug(slug);
  const interval = parseUpdownIntervalSeconds(parsed?.interval || round?.interval || '5m');
  // Prefer endDate / liquiditySource reconcile so PM start-embed slugs are not
  // misread as SELF end-embed (collapses adjacent 5m slots → strip skip).
  const viewing = resolveViewingUpdownRound(round, slug, interval);
  const fromViewing = Number(viewing?.roundTs);
  const slugEnd = Number(viewing?.endSec);
  const explicit = Number(round?.roundTs);
  const slugUnix = Number(parsed?.slugUnix);

  if (Number.isFinite(explicit) && explicit > 0) {
    if (!fromViewing || isUpdownRoundTsNear(fromViewing, explicit, 90)) {
      return fromViewing || explicit;
    }
    // PM start-embed：mapRaw 已写入开盘 = slugUnix；空 venue 的 end-embed viewing
    // 会把 endSec 设成 slugUnix，旧分支误判「roundTs=收盘」并退回上一档开盘
    // → pick live 命中下一档自身，进页不纠偏（TEST 2026-09-14）。
    if (
      parsed?.kind === 'interval' &&
      slugUnix > 0 &&
      isUpdownRoundTsNear(slugUnix, explicit, 90)
    ) {
      return explicit;
    }
    // 兼容误把 slug 收盘 ts 写进 roundTs：纠正为开盘
    if (slugEnd && isUpdownRoundTsNear(slugEnd, explicit, 90)) {
      return fromViewing;
    }
    return explicit;
  }

  return Number.isFinite(fromViewing) && fromViewing > 0 ? fromViewing : 0;
}

export function formatCountdownMs(diffMs) {
  if (!Number.isFinite(diffMs) || diffMs <= 0) return '00:00';
  const totalSec = Math.floor(diffMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}
